import { Controller } from "@hotwired/stimulus"

// Enhanced Form validation controller for financial services forms
// Supports real-time validation, autosave, and accessibility features
export default class extends Controller {
  static targets = ["field", "submitButton", "passwordField", "passwordConfirmationField", "input", "error", "validIcon", "charCount"]
  static values = { 
    requireAll: { type: Boolean, default: true },
    minPasswordLength: { type: Number, default: 8 },
    autosave: { type: Boolean, default: false },
    autosaveInterval: { type: Number, default: 30000 }
  }

  connect() {
    this.setupValidationRules()
    this.addFieldListeners()
    this.setupAutosave()
    // Validate form after a brief delay to ensure all targets are connected
    setTimeout(() => this.validateForm(), 100)
  }

  disconnect() {
    if (this.autosaveTimer) {
      clearInterval(this.autosaveTimer)
    }
  }

  setupValidationRules() {
    this.validationRules = {
      required: (value) => value && value.trim().length > 0,
      email: (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
      minLength: (value, min) => value.length >= parseInt(min),
      maxLength: (value, max) => value.length <= parseInt(max),
      pattern: (value, pattern) => new RegExp(pattern).test(value),
      url: (value) => {
        try {
          new URL(value)
          return true
        } catch {
          return false
        }
      }
    }
  }

  setupAutosave() {
    if (!this.autosaveValue) return

    this.autosaveTimer = setInterval(() => {
      if (this.hasUnsavedChanges()) {
        this.performAutosave()
      }
    }, this.autosaveIntervalValue)
  }

  addFieldListeners() {
    // Handle legacy field targets
    this.fieldTargets.forEach(field => {
      field.addEventListener("input", () => this.validateForm())
      field.addEventListener("change", () => this.validateForm())
      field.addEventListener("blur", () => this.validateField(field))
    })

    // Handle enhanced input targets with real-time validation
    this.inputTargets.forEach(input => {
      input.addEventListener('input', (e) => {
        this.validateEnhancedField(e.target)
        this.updateCharacterCount(e.target)
        this.markAsModified(e.target)
      })

      input.addEventListener('blur', (e) => {
        this.validateEnhancedField(e.target)
      })

      input.addEventListener('focus', (e) => {
        this.clearValidationState(e.target)
      })
    })

    // Special handling for password confirmation
    if (this.hasPasswordFieldTarget && this.hasPasswordConfirmationFieldTarget) {
      this.passwordConfirmationFieldTarget.addEventListener("input", () => {
        this.validatePasswordMatch()
      })
    }
  }

  validateForm() {
    const isValid = this.isFormValid()
    const validationErrors = this.getValidationErrors()
    
    
    // Update submit button state based on form validity
    if (this.hasSubmitButtonTarget) {
      if (isValid) {
        this.enableSubmitButton()
      } else {
        this.disableSubmitButton()
      }
    }

    // Dispatch custom event for other controllers (like Turnstile)
    this.dispatch("validation-changed", { 
      detail: { 
        isValid: isValid,
        validationErrors: validationErrors
      }
    })

    return isValid
  }

  validateField(field) {
    const isValid = this.isFieldValid(field)
    this.updateFieldUI(field, isValid)
    return isValid
  }

  isFormValid() {
    // Check if all required fields are filled
    const requiredFieldsValid = this.fieldTargets.every(field => {
      return this.isFieldValid(field)
    })

    // Check password confirmation if present
    const passwordsMatch = this.passwordsMatch()

    return requiredFieldsValid && passwordsMatch
  }

  isFieldValid(field) {
    const fieldName = field.name || field.id || 'unknown'

    // Special handling for readonly fields (like pre-filled email)
    if (field.readOnly || field.readonly) {
      const isValid = field.value.trim() !== ''
      // console.log(`📧 Readonly field ${fieldName}: isValid=${isValid}, value="${field.value}"`)
      return isValid
    }

    if (field.type === 'radio') {
      if (field.required) {
        const form = field.closest('form')
        const radioGroup = form ? form.querySelectorAll(`input[type="radio"][name="${field.name}"]`) : []
        return Array.from(radioGroup).some(radio => radio.checked)
      }
      return true
    }

    // Check if field is required and empty
    if (field.required && !field.value.trim()) {
      // console.log(`❌ Required field ${fieldName} is empty`)
      return false
    }

    // Check email format for email fields with content
    if (field.type === "email" && field.value.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      const isValidEmail = emailRegex.test(field.value)
      if (!isValidEmail) {
        // console.log(`📧 Email field ${fieldName} has invalid format: ${field.value}`)
        return false
      }
    }

    // Check password length for password fields
    if (field.type === "password" && field.required) {
      const isValidLength = field.value.length >= this.minPasswordLengthValue
      if (!isValidLength) {
        // console.log(`🔒 Password field ${fieldName} too short: ${field.value.length} < ${this.minPasswordLengthValue}`)
        return false
      }
    }

    // Check minimum length attribute
    if (field.minLength && field.value.length > 0 && field.value.length < field.minLength) {
      // console.log(`📏 Field ${fieldName} below minLength: ${field.value.length} < ${field.minLength}`)
      return false
    }

    // console.log(`✅ Field ${fieldName} is valid`)
    return true
  }

  passwordsMatch() {
    if (!this.hasPasswordFieldTarget || !this.hasPasswordConfirmationFieldTarget) {
      // console.log("🔒 No password fields found - passwords match by default")
      return true
    }

    const password = this.passwordFieldTarget.value
    const confirmation = this.passwordConfirmationFieldTarget.value

    // console.log("🔒 Password match check:", {
    //   passwordLength: password ? password.length : 0,
    //   confirmationLength: confirmation ? confirmation.length : 0,
    //   confirmationRequired: this.passwordConfirmationFieldTarget.required,
    //   bothEmpty: !password && !confirmation,
    //   bothFilled: !!password && !!confirmation,
    //   match: password === confirmation
    // })

    // If password confirmation is required and either field is empty, passwords don't match
    if (this.passwordConfirmationFieldTarget.required) {
      if (!password || !confirmation) {
        // console.log("🔒 Required password fields are empty - no match")
        return false
      }
    }

    // If both fields have values, they must match
    if (password && confirmation) {
      const match = password === confirmation
      // console.log(`🔒 Both fields filled - match: ${match}`)
      return match
    }

    // If neither field has a value, they "match" (but form may still be invalid due to required validation)
    if (!password && !confirmation) {
      // console.log("🔒 Both fields empty - match by default")
      return true
    }

    // One field has a value and the other doesn't - they don't match
    // console.log("🔒 Only one field filled - no match")
    return false
  }

  validatePasswordMatch() {
    if (!this.hasPasswordFieldTarget || !this.hasPasswordConfirmationFieldTarget) {
      return
    }

    const matches = this.passwordsMatch()
    this.updatePasswordMatchUI(matches)
    this.validateForm() // Revalidate entire form
  }

  updateFieldUI(field, isValid) {
    const fieldContainer = field.closest('.field-container') || field.parentElement

    if (isValid) {
      field.classList.remove('border-red-500', 'focus:ring-red-500', 'focus:border-red-500')
      field.classList.add('border-gray-300', 'focus:ring-blue-500', 'focus:border-blue-500')
      this.clearFieldError(fieldContainer)
    } else {
      field.classList.remove('border-gray-300', 'focus:ring-blue-500', 'focus:border-blue-500')
      field.classList.add('border-red-500', 'focus:ring-red-500', 'focus:border-red-500')
      this.showFieldError(field, fieldContainer)
    }
  }

  updatePasswordMatchUI(matches) {
    if (!this.hasPasswordConfirmationFieldTarget) return

    const field = this.passwordConfirmationFieldTarget
    const fieldContainer = field.closest('.field-container') || field.parentElement

    if (matches) {
      field.classList.remove('border-red-500', 'focus:ring-red-500', 'focus:border-red-500')
      field.classList.add('border-gray-300', 'focus:ring-blue-500', 'focus:border-blue-500')
      this.clearFieldError(fieldContainer)
    } else {
      field.classList.remove('border-gray-300', 'focus:ring-blue-500', 'focus:border-blue-500')
      field.classList.add('border-red-500', 'focus:ring-red-500', 'focus:border-red-500')
      this.showPasswordMatchError(fieldContainer)
    }
  }

  showFieldError(field, container) {
    // Don't show errors for readonly fields
    if (field.readOnly || field.readonly) {
      return
    }

    let errorMsg = ""
    
    if (field.required && !field.value.trim()) {
      errorMsg = "This field is required"
    } else if (field.type === "email" && field.value.trim()) {
      errorMsg = "Please enter a valid email address"
    } else if (field.type === "password" && field.value.length < this.minPasswordLengthValue) {
      errorMsg = `Password must be at least ${this.minPasswordLengthValue} characters`
    }

    this.showError(container, errorMsg)
  }

  showPasswordMatchError(container) {
    this.showError(container, "Passwords do not match")
  }

  showError(container, message) {
    if (!message) return

    this.clearFieldError(container)
    
    const errorDiv = document.createElement('div')
    errorDiv.className = 'field-error text-sm text-red-600 mt-1'
    errorDiv.textContent = message
    container.appendChild(errorDiv)
  }

  clearFieldError(container) {
    const existingError = container.querySelector('.field-error')
    if (existingError) {
      existingError.remove()
    }
  }

  enableSubmitButton() {
    if (!this.hasSubmitButtonTarget) return

    const form = this.element
    const turnstileElement = form.querySelector('[data-controller*="turnstile"]')
    const isInvisibleTurnstile = turnstileElement?.dataset.turnstileSizeValue === "invisible"

    if (turnstileElement && !isInvisibleTurnstile) {
      this.dispatch("form-valid", { detail: { isValid: true } })
    } else {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.classList.remove('btn-disabled')
      this.submitButtonTarget.classList.add('btn-enabled')
    }
  }

  disableSubmitButton() {
    if (!this.hasSubmitButtonTarget) return

    const form = this.element
    const turnstileElement = form.querySelector('[data-controller*="turnstile"]')
    const isInvisibleTurnstile = turnstileElement?.dataset.turnstileSizeValue === "invisible"

    if (turnstileElement && !isInvisibleTurnstile) {
      this.dispatch("form-valid", { detail: { isValid: false } })
    } else {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.classList.add('btn-disabled')
      this.submitButtonTarget.classList.remove('btn-enabled')
    }
  }

  getValidationErrors() {
    const errors = []
    
    this.fieldTargets.forEach(field => {
      if (!this.isFieldValid(field)) {
        errors.push({
          field: field.name || field.id,
          message: this.getFieldErrorMessage(field)
        })
      }
    })

    if (!this.passwordsMatch()) {
      errors.push({
        field: 'password_confirmation',
        message: 'Passwords do not match'
      })
    }

    return errors
  }

  getFieldErrorMessage(field) {
    if (field.required && !field.value.trim()) {
      return "This field is required"
    } else if (field.type === "email" && field.value.trim()) {
      return "Please enter a valid email address"
    } else if (field.type === "password" && field.value.length < this.minPasswordLengthValue) {
      return `Password must be at least ${this.minPasswordLengthValue} characters`
    }
    return "Invalid input"
  }

  // Method for external controllers to check form validity
  isValid() {
    return this.isFormValid()
  }

  // Method to manually trigger validation
  validate() {
    return this.validateForm()
  }

  // Enhanced validation methods for new form features
  validateEnhancedField(input) {
    const errors = []
    const value = input.value

    // Get validation rules from data attributes
    const rules = this.getValidationRules(input)

    // Required validation
    if (rules.required && !this.validationRules.required(value)) {
      errors.push("This field is required")
    }

    // Skip other validations if field is empty and not required
    if (!value && !rules.required) {
      this.showValidationState(input, true, [])
      return true
    }

    // Email validation
    if (rules.email && value && !this.validationRules.email(value)) {
      errors.push("Please enter a valid email address")
    }

    // Pattern validation  
    if (rules.pattern && value && !this.validationRules.pattern(value, rules.pattern)) {
      errors.push(rules.patternMessage || "Invalid format")
    }

    // Length validation
    if (rules.minLength && value && !this.validationRules.minLength(value, rules.minLength)) {
      errors.push(`Minimum ${rules.minLength} characters required`)
    }

    if (rules.maxLength && value && !this.validationRules.maxLength(value, rules.maxLength)) {
      errors.push(`Maximum ${rules.maxLength} characters allowed`)
    }

    // URL validation
    if (rules.url && value && !this.validationRules.url(value)) {
      errors.push("Please enter a valid URL")
    }

    const isValid = errors.length === 0
    this.showValidationState(input, isValid, errors)
    return isValid
  }

  getValidationRules(input) {
    return {
      required: input.dataset.formValidatorRequired === "true",
      email: input.dataset.formValidatorType === "email",
      pattern: input.dataset.formValidatorPattern,
      patternMessage: input.dataset.formValidatorPatternMessage,
      minLength: input.dataset.formValidatorMinLength,
      maxLength: input.dataset.formValidatorMaxLength,
      url: input.dataset.formValidatorType === "url"
    }
  }

  showValidationState(input, isValid, errors) {
    const errorElement = this.findErrorElement(input)
    const validIcon = this.findValidIcon(input)

    if (isValid) {
      // Show success state
      input.classList.remove('border-red-500', 'focus:border-red-500', 'focus:ring-red-500')
      input.classList.add('border-green-500', 'focus:border-green-500', 'focus:ring-green-500')
      
      if (errorElement) {
        errorElement.classList.add('hidden')
        errorElement.textContent = ''
      }
      
      if (validIcon) {
        validIcon.classList.remove('hidden')
      }
    } else {
      // Show error state
      input.classList.remove('border-green-500', 'focus:border-green-500', 'focus:ring-green-500')
      input.classList.add('border-red-500', 'focus:border-red-500', 'focus:ring-red-500')
      
      if (errorElement) {
        errorElement.classList.remove('hidden')
        errorElement.textContent = errors[0] || 'Invalid input'
      }
      
      if (validIcon) {
        validIcon.classList.add('hidden')
      }
    }

    // Update ARIA attributes for accessibility
    input.setAttribute('aria-invalid', !isValid)
    if (!isValid && errors.length > 0) {
      input.setAttribute('aria-describedby', errorElement?.id || '')
    }
  }

  clearValidationState(input) {
    input.classList.remove(
      'border-red-500', 'focus:border-red-500', 'focus:ring-red-500',
      'border-green-500', 'focus:border-green-500', 'focus:ring-green-500'
    )
    
    const errorElement = this.findErrorElement(input)
    const validIcon = this.findValidIcon(input)
    
    if (errorElement) {
      errorElement.classList.add('hidden')
    }
    
    if (validIcon) {
      validIcon.classList.add('hidden')
    }
  }

  findErrorElement(input) {
    const container = input.closest('.space-y-1') || input.closest('.relative')?.parentElement
    return container?.querySelector('[data-form-validator-target="error"]')
  }

  findValidIcon(input) {
    const container = input.closest('.space-y-1') || input.closest('.relative')
    return container?.querySelector('[data-form-validator-target="validIcon"]')
  }

  updateCharacterCount(input) {
    const maxLength = input.dataset.formValidatorMaxLength
    if (!maxLength) return

    const charCountElement = this.findCharCountElement(input)
    if (charCountElement) {
      const currentLength = input.value.length
      charCountElement.textContent = currentLength
      
      // Update color based on character usage
      const percentage = (currentLength / parseInt(maxLength)) * 100
      charCountElement.classList.remove('text-red-500', 'text-yellow-500', 'text-gray-400')
      
      if (percentage >= 90) {
        charCountElement.classList.add('text-red-500')
      } else if (percentage >= 70) {
        charCountElement.classList.add('text-yellow-500')
      } else {
        charCountElement.classList.add('text-gray-400')
      }
    }
  }

  findCharCountElement(input) {
    const container = input.closest('.space-y-1') || input.closest('.relative').parentElement
    return container?.querySelector('[data-form-validator-target="charCount"]')
  }

  markAsModified(input) {
    input.dataset.modified = "true"
    this.element.dataset.hasUnsavedChanges = "true"
  }

  hasUnsavedChanges() {
    return this.element.dataset.hasUnsavedChanges === "true"
  }

  async performAutosave() {
    if (!this.validateAllEnhancedFields()) {
      console.log("Autosave skipped: validation errors present")
      return
    }

    try {
      const formData = new FormData(this.element)
      formData.append('autosave', 'true')

      const response = await fetch(this.element.action, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': this.getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      })

      if (response.ok) {
        this.element.dataset.hasUnsavedChanges = "false"
        this.showAutosaveNotification("Draft saved automatically")
      }
    } catch (error) {
      console.error("Autosave failed:", error)
    }
  }

  validateAllEnhancedFields() {
    let allValid = true
    this.inputTargets.forEach(input => {
      if (!this.validateEnhancedField(input)) {
        allValid = false
      }
      this.updateCharacterCount(input)
    })
    return allValid
  }

  showAutosaveNotification(message) {
    const notification = document.createElement('div')
    notification.className = 'fixed top-4 right-4 bg-green-100 border border-green-200 rounded-lg p-3 text-sm text-green-700 z-50 transition-all duration-300 opacity-0'
    notification.textContent = message

    document.body.appendChild(notification)

    setTimeout(() => notification.classList.remove('opacity-0'), 100)
    setTimeout(() => {
      notification.classList.add('opacity-0')
      setTimeout(() => notification.remove(), 300)
    }, 3000)
  }

  getCSRFToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}